import {Log} from '../logger/log';
import {getIsStorageConsentGiven} from './consentBannerAcceptedState';
import {getValueToPersist} from './getValueToPersist';

type ValueState = {
  value: unknown;
  valueAfterCookieConsent: unknown;
};

const inMemoryLocalStorageStore = new Map<string, ValueState>();

export const localStorageSet = ({
  key,
  data,
  /** Setting this to true will force save the value in localStorage even when there is no cookie consent for doing so.
   * This is in general a legal thread and can only be used if agreed with the legal department.
   * It will be mandatory to adjust the consent banner text for all countries before the change can go online.
   * Please use this only if it's strictly mandatory to make the ABOUT YOU website work
   */
  forcePersistValue_ILLEGALLY,
}: {
  key: string;
  data: unknown;
  forcePersistValue_ILLEGALLY?: boolean;
}): void => {
  try {
    if (typeof window === 'undefined') return;

    const canPersist =
      getIsStorageConsentGiven() || forcePersistValue_ILLEGALLY;

    const valueToStore = {
      value: canPersist ? inMemoryLocalStorageStore.get(key)?.value : data,
      valueAfterCookieConsent: canPersist ? data : undefined,
    };

    inMemoryLocalStorageStore.set(key, valueToStore);

    if (canPersist) {
      const value = getValueToPersist({
        valueBeforeCookieConsent: valueToStore.value,
        valueAfterCookieConsent: valueToStore.valueAfterCookieConsent,
      });
      window.localStorage.setItem(key, value);
    }
  } catch (err) {
    Log.error(err);
  }
};

export const localStorageGet = (
  key: string,
  forceReadValue_ILLEGALLY = false,
) => {
  try {
    if (typeof window === 'undefined') return null;

    const inMemoryValue =
      inMemoryLocalStorageStore.get(key)?.valueAfterCookieConsent ??
      inMemoryLocalStorageStore.get(key)?.value;

    if (inMemoryValue) {
      return inMemoryValue;
    }

    const readFromLocalStorage =
      getIsStorageConsentGiven() || forceReadValue_ILLEGALLY;

    const item = readFromLocalStorage
      ? window.localStorage?.getItem(key)
      : undefined;

    if (!item) {
      return null;
    }

    try {
      return JSON.parse(item);
    } catch (err) {
      return item;
    }
  } catch (err) {
    Log.error(err);
  }

  return null;
};

export const localStorageRemove = (key: string): void => {
  try {
    if (typeof window === 'undefined') return;

    window.sessionStorage.removeItem(key);
    inMemoryLocalStorageStore.delete(key);
  } catch (err) {
    Log.error(err);
  }
};

export const localStorageClear = (): void => {
  try {
    if (typeof window === 'undefined') return;

    window.localStorage.clear();
    inMemoryLocalStorageStore.clear();
  } catch (err) {
    Log.error(err);
  }
};
